import type { SqlDriver } from './db/driver';
import { getMeta, setMeta } from './appMeta';
import { currencyExponent } from '../domain/money';
import type { IsoDate } from '../domain/model';

/**
 * Exchange rates for entries paid in another currency: the European Central
 * Bank's daily reference rates through Frankfurter (free, no key; only the
 * two currency codes and the date are sent). A rate is kept on the device
 * once fetched, so the same day works offline and a new day offline falls
 * back to the last rate known for that pair — shown as such.
 */
export const FX_SOURCE = 'https://api.frankfurter.dev/v1';

/** Currencies the ECB publishes a reference rate for; others are converted by hand. */
export const ECB_CURRENCIES: ReadonlySet<string> = new Set([
  'AUD', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF', 'IDR', 'ILS', 'INR', 'ISK',
  'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB', 'TRY', 'USD', 'ZAR',
]);

export interface FxRate {
  /** 1 unit of `from` is `rate` units of `to`. */
  rate: number;
  /** The ECB business day the rate is for (the nearest one on or before the date asked). */
  date: IsoDate;
  /** Not for the date asked: the last one known on this device (offline). */
  stale: boolean;
}

type Fetch = (url: string, init?: { signal?: AbortSignal }) => Promise<{ ok: boolean; json(): Promise<unknown> }>;

const key = (from: string, to: string, date: IsoDate | 'latest') => `fx.${from}.${to}.${date}`;

export function hasPublishedRate(from: string, to: string): boolean {
  return ECB_CURRENCIES.has(from) && ECB_CURRENCIES.has(to);
}

/** The rate for paying in `from` on `date`, with the entry in `to`; null when none is known (then the user types the converted amount). */
export async function rateFor(db: SqlDriver, from: string, to: string, date: IsoDate, today: IsoDate, fetchImpl: Fetch = fetch as unknown as Fetch): Promise<FxRate | null> {
  if (from === to) return { rate: 1, date, stale: false };
  if (!hasPublishedRate(from, to)) return null;
  // Today's rate is published in the afternoon (CET): ask for the latest, never for a date not yet out.
  const asked = date >= today ? 'latest' : date;
  const cached = await getMeta(db, key(from, to, asked === 'latest' ? today : asked));
  if (cached) return { ...(JSON.parse(cached) as Omit<FxRate, 'stale'>), stale: false };
  try {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 5000);
    const res = await fetchImpl(`${FX_SOURCE}/${asked}?base=${from}&symbols=${to}`, { signal: abort.signal }).finally(() => clearTimeout(timer));
    if (!res.ok) throw new Error('rate not available');
    const body = (await res.json()) as { date?: string; rates?: Record<string, number> };
    const rate = body.rates?.[to];
    if (typeof rate !== 'number' || !(rate > 0) || !body.date) throw new Error('unexpected rate response');
    const found = JSON.stringify({ rate, date: body.date });
    await setMeta(db, key(from, to, asked === 'latest' ? today : asked), found);
    await setMeta(db, key(from, to, 'latest'), found);
    return { rate, date: body.date, stale: false };
  } catch {
    const last = await getMeta(db, key(from, to, 'latest'));
    return last ? { ...(JSON.parse(last) as Omit<FxRate, 'stale'>), stale: true } : null;
  }
}

/** `minor` units of `from` in minor units of `to` at `rate` (exponents may differ: JPY 0, USD 2, KWD 3). */
export function convertMinor(minor: number, from: string, to: string, rate: number): number {
  const value = (minor / 10 ** currencyExponent(from)) * rate;
  return Math.max(1, Math.round(value * 10 ** currencyExponent(to)));
}
